/* Yen Aura - Build Your Own Bundle interface.

   OUR interface on top of Simple Bundles' engine.

   The app's builder is kept in the DOM (moved off-screen by CSS) for two
   reasons: it is the source of truth for the product list, the per-variant
   `hash`, the pack sizes and the discount percentages, and its server side is
   what actually prices the cart line. We never hard-code any of that, so if the
   owner changes tiers, discounts or products inside the app, this interface
   follows automatically.

   Adding to cart is a plain /cart/add.js with the pack's variant id and
   properties._bundle_selection set to the chosen hashes joined by " <> ".
   Verified: A$114.85 of product bills at A$103.38 on the 10% tier.

   v2.9.9 CRO pass (15 Sep 2026):
   - tier picker sits ABOVE the product grid on phones, always visible
   - compact sticky bar on phones (progress, running total, saving, CTA)
   - live saving from the first pick, plus a "make it 4 / 5" upgrade nudge
   - in-bundle price on every card, tap anywhere on a card to add
   - free gift and free shipping shown as a ladder with "A$X to go"
   - one-tap "start with our best sellers" quick fill
   - rating and guarantee line under the button

   v3 (15 Sep 2026): OPEN "5+ items" TIER.
   Simple Bundles packs are fixed counts (one variant each). To offer "5 or more"
   the owner creates packs 5, 6, 7, ... in the app. This script groups every pack
   from `openFrom` (default 5) upward into ONE tier labelled "5+ items" whose slot
   count grows as items are added. On add to cart the picks are split into pack
   lines of sizes the app actually has: 8 picks = one "8 Pack" line, 12 picks =
   two "6 Pack" lines, and so on. Every line carries the same discount, so the
   total is identical to one big line. The open tier only switches on when the
   app has a contiguous run of packs from openFrom to at least 2*openFrom-1
   (5..9), which is what guarantees any count is composable. Until then the
   page shows the fixed tiers exactly as before. */
(function () {
  'use strict';

  var MOUNT_ID = 'yenb-builder';
  var POLL_MS = 150;
  var TIMEOUT_MS = 20000;
  var MQ = window.matchMedia('(max-width:989px)');

  function isMobile() { return MQ.matches; }
  function money(cents) { return 'A$' + (cents / 100).toFixed(2); }
  function readJson(id, fallback) {
    var tag = document.getElementById(id);
    if (!tag) return fallback;
    try { return JSON.parse(tag.textContent) || fallback; } catch (e) { return fallback; }
  }

  function config() {
    var d = readJson('yenb-config', {});
    return {
      pickerHeading: d.pickerHeading || 'Pick your favourites!',
      quickText: d.quickText || 'Not sure? Start with our best sellers',
      giftCents: Number(d.giftCents) || 4900,
      giftText: d.giftText || 'Free Soap Saver Bag included',
      shipCents: Number(d.shipCents) || 9900,
      shipText: d.shipText || 'Free shipping included',
      ratingText: d.ratingText || '4.8/5 from 20,000+ happy customers',
      guaranteeText: d.guaranteeText || '90-Day Money-Back Guarantee',
      openFrom: Number(d.openFrom) || 5
    };
  }

  /* ---------- read the app's data ---------- */

  function readPacks() {
    var seen = {}, out = [];
    Array.prototype.forEach.call(document.querySelectorAll('byob-pack'), function (el) {
      var vid = el.variantId;
      if (!vid || seen[vid]) return;
      seen[vid] = 1;
      out.push({
        label: el.label || (el.itemCount + ' Pack'),
        count: Number(el.itemCount) || 0,
        discount: Number(el.discountPercent) || 0,
        variantId: String(vid)
      });
    });
    out.sort(function (a, b) { return a.count - b.count; });
    return out.filter(function (p) { return p.count > 0; });
  }

  function readProducts() {
    var seen = {}, out = [];
    Array.prototype.forEach.call(document.querySelectorAll('byob-product-picker-card'), function (el) {
      var p = el.product;
      if (!p || !p.variants || !p.variants.length) return;
      var v = null;
      for (var i = 0; i < p.variants.length; i++) {
        if (p.variants[i].available !== false) { v = p.variants[i]; break; }
      }
      if (!v) v = p.variants[0];
      if (!v.hash || seen[v.hash]) return;
      if (v.available === false) return;          // out of stock never shown
      if (p.variants.length > 1) return;          // multi-variant needs the app's own modal
      seen[v.hash] = 1;
      var img = v.imageUrl || p.imageUrl || '';
      if (img.indexOf('//') === 0) img = 'https:' + img;
      out.push({
        title: p.title || '',
        handle: (p.productUrl || '').split('?')[0].split('/').pop(),
        img: img,
        hash: v.hash,
        price: Number(v.compareAtPriceCents || v.priceCents) || 0
      });
    });
    return out;
  }

  function dataReady() {
    var cards = document.querySelectorAll('byob-product-picker-card');
    if (!cards.length || !document.querySelectorAll('byob-pack').length) return false;
    for (var i = 0; i < cards.length; i++) { if (!cards[i].product) return false; }
    return true;
  }

  /* ---------- tiers: fixed packs below openFrom, one open tier above ---------- */

  function buildTiers(packs, openFrom) {
    var byCount = {};
    packs.forEach(function (p) { byCount[p.count] = p; });
    var tiers = [];
    function fixed(p) {
      return { kind: 'fixed', label: p.count + ' items', min: p.count, count: p.count,
               discount: p.discount, packs: [p], maxPack: p.count };
    }
    packs.forEach(function (p) { if (p.count < openFrom) tiers.push(fixed(p)); });

    var run = [];
    for (var c = openFrom; byCount[c]; c++) run.push(byCount[c]);
    var maxPack = run.length ? run[run.length - 1].count : 0;

    if (run.length && maxPack >= 2 * openFrom - 1) {
      var lo = run[0].discount, hi = run[0].discount;
      run.forEach(function (p) { lo = Math.min(lo, p.discount); hi = Math.max(hi, p.discount); });
      tiers.push({ kind: 'open', label: openFrom + '+ items', min: openFrom, count: Infinity,
                   discount: run[0].discount, discountHi: hi, packs: run, maxPack: maxPack });
    } else {
      // Not enough packs in the app yet: every pack stays a fixed tier.
      packs.forEach(function (p) { if (p.count >= openFrom) tiers.push(fixed(p)); });
    }
    return tiers;
  }

  /* Split the picks into pack lines the app actually has. Evenly sized so no
     line ever drops below openFrom (guaranteed by maxPack >= 2*openFrom-1). */
  function composeLines(tier, picks) {
    if (tier.kind === 'fixed') return [{ pack: tier.packs[0], items: picks }];
    var byCount = {};
    tier.packs.forEach(function (p) { byCount[p.count] = p; });
    var n = picks.length;
    var k = Math.max(1, Math.ceil(n / tier.maxPack));
    var base = Math.floor(n / k), extra = n - base * k;
    var lines = [], i = 0;
    for (var j = 0; j < k; j++) {
      var size = base + (j < extra ? 1 : 0);
      if (!byCount[size]) return null;
      lines.push({ pack: byCount[size], items: picks.slice(i, i + size) });
      i += size;
    }
    return lines;
  }

  function roundItem(cents, discount) {
    // The app rounds each item's discounted price, then sums. Matching that
    // exactly is what keeps our totals identical to the cart.
    return Math.round(cents * (100 - discount) / 100);
  }

  /* ---------- build ---------- */

  function build() {
    var packs = readPacks();
    var products = readProducts();
    var notes = readJson('yenb-blurbs', {});
    var best = readJson('yenb-bestsellers', []);
    var cfg = config();
    if (!packs.length || !products.length) return false;
    var tiers = buildTiers(packs, cfg.openFrom);
    if (!tiers.length) return false;

    // Mount OUTSIDE <byob-bundle>: it is a Stencil component and hides any
    // child it does not own, which silently made our interface display:none.
    var host = document.querySelector('byob-bundle');
    if (!host) return false;

    // Mount straight after the HERO section, not before the product section.
    // main-product.liquid leaves a <div> and a <section> unclosed, so the
    // browser nests every following section inside it, and Simple Bundles sets
    // that section to display:none. Anything after it therefore disappears.
    // The product section is last in this template for the same reason.
    var hero = document.querySelector('.yenb-hero');
    var heroSection = hero && hero.closest ? hero.closest('[id^="shopify-section"]') : null;
    var anchor, before;
    if (heroSection && heroSection.parentNode) {
      anchor = heroSection.parentNode; before = heroSection.nextSibling;
    } else {
      var sec = host.closest('[id^="shopify-section"]') || host;
      if (!sec.parentNode) return false;
      anchor = sec.parentNode; before = sec;
    }

    var existing = document.getElementById(MOUNT_ID);
    if (existing) existing.parentNode.removeChild(existing);

    var root = document.createElement('div');
    root.id = MOUNT_ID;
    root.className = 'yenb';
    anchor.insertBefore(root, before);

    var tier = tiers[0];
    var bestTier = tiers[tiers.length - 1];
    var picks = [];
    var autoOpened = false;

    root.innerHTML =
      '<div class="yenb-wrap"><div class="yenb-grid">' +
        '<div class="yenb-main">' +
          '<div class="yenb-head">' +
            '<span class="yenb-eyebrow">Mix and match</span>' +
            '<h2 class="yenb-h2" data-picker-heading></h2>' +
            '<p class="yenb-sub" data-picker-sub></p>' +
            '<button type="button" class="yenb-quick" data-quick></button>' +
          '</div>' +
          '<div class="yenb-tierbar" data-tiers></div>' +
          '<div class="yenb-cards" data-cards></div>' +
          '<div class="yenb-viewwrap"><button type="button" class="yenb-view" data-view></button></div>' +
        '</div>' +
        '<aside class="yenb-panel" data-panel data-open="true">' +
          '<button type="button" class="yenb-close" data-close aria-label="Close your bundle">&times;</button>' +
          '<div class="yenb-panel__head"><h3>Your bundle</h3><span class="yenb-count" data-count></span></div>' +
          '<div class="yenb-bar"><i data-bar></i></div>' +
          '<div class="yenb-label yenb-label--size">Bundle size</div>' +
          '<div class="yenb-tiers" data-tiers></div>' +
          '<div class="yenb-label">Your picks</div>' +
          '<div class="yenb-slots" data-slots></div>' +
          '<p class="yenb-empty" data-empty>Tap a product to start. Your saving updates as you go.</p>' +
          '<div class="yenb-totals" data-totals hidden>' +
            '<div class="yenb-row"><span>Full price</span><s data-sub></s></div>' +
            '<div class="yenb-row is-save"><span data-save-label>Bundle saving</span><span data-save></span></div>' +
            '<div class="yenb-row is-total"><span>Bundle total</span><span data-total></span></div>' +
          '</div>' +
          '<ul class="yenb-perks" data-perks hidden>' +
            '<li data-gift><i></i><span data-gift-text></span><small data-gift-togo></small></li>' +
            '<li data-ship><i></i><span data-ship-text></span><small data-ship-togo></small></li>' +
          '</ul>' +
          '<div class="yenb-nudge" data-nudge hidden><span data-nudge-text></span>' +
            '<button type="button" data-nudge-btn></button></div>' +
          '<button type="button" class="yenb-cta" data-cta disabled></button>' +
          '<p class="yenb-trust"><b>&#9733; ' + cfg.ratingText + '</b><br>' + cfg.guaranteeText + '</p>' +
        '</aside>' +
      '</div></div>' +
      '<div class="yenb-backdrop" data-backdrop></div>' +
      '<div class="yenb-sticky" data-sticky>' +
        '<i class="yenb-sticky__grip" aria-hidden="true"></i>' +
        '<button type="button" class="yenb-sticky__info" data-sticky-open aria-label="Open your bundle">' +
          '<b><span data-sticky-title></span>' +
          '<svg viewBox="0 0 20 20" width="14" height="14" fill="none" aria-hidden="true">' +
          '<path d="M5 12.5l5-5 5 5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></b>' +
          '<span data-sticky-sub></span></button>' +
        '<button type="button" class="yenb-sticky__cta" data-sticky-cta></button>' +
      '</div>';

    root.querySelector('[data-picker-heading]').textContent = cfg.pickerHeading;
    root.querySelector('[data-picker-sub]').textContent =
      products.length + ' to choose from. The more you add, the more you save.';
    root.querySelector('[data-gift-text]').textContent = cfg.giftText;
    root.querySelector('[data-ship-text]').textContent = cfg.shipText;

    var elCards = root.querySelector('[data-cards]');
    var elTierBoxes = root.querySelectorAll('[data-tiers]');
    var elSlots = root.querySelector('[data-slots]');
    var elCount = root.querySelector('[data-count]');
    var elCta = root.querySelector('[data-cta]');
    var elQuick = root.querySelector('[data-quick]');
    var panel = root.querySelector('[data-panel]');
    var sticky = root.querySelector('[data-sticky]');
    var stickyCta = root.querySelector('[data-sticky-cta]');
    var nudge = root.querySelector('[data-nudge]');
    var elView = root.querySelector('[data-view]');
    var nudgeBtn = root.querySelector('[data-nudge-btn]');

    function isOpen() { return tier.kind === 'open'; }
    function isComplete() { return isOpen() ? picks.length >= tier.min : picks.length === tier.count; }
    function isFull() { return !isOpen() && picks.length >= tier.count; }
    function needed() { return Math.max(tier.min - picks.length, 0); }
    function tierSaveLabel(t) {
      return t.kind === 'open' && t.discountHi > t.discount
        ? 'Save up to ' + t.discountHi + '%' : 'Save ' + t.discount + '%';
    }

    /* ---------- tiers: rendered in both places, one source of truth ---------- */
    function setTier(t) {
      tier = t;
      if (!isOpen() && picks.length > tier.count) picks.length = tier.count;
      render();
    }
    function renderTiers() {
      Array.prototype.forEach.call(elTierBoxes, function (box) {
        box.innerHTML = '';
        tiers.forEach(function (t) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'yenb-tier';
          b.setAttribute('aria-pressed', String(t === tier));
          b.innerHTML =
            (t === bestTier && tiers.length > 1 ? '<em>BEST VALUE</em>' : '') +
            '<b>' + t.label + '</b><small>' + tierSaveLabel(t) + '</small>';
          b.addEventListener('click', function () { setTier(t); });
          box.appendChild(b);
        });
      });
    }

    /* ---------- cards ---------- */
    products.forEach(function (p) {
      var el = document.createElement('article');
      el.className = 'yenb-card';
      var note = notes[p.handle] ? '<p class="yenb-card__desc">' + notes[p.handle] + '</p>' : '';
      el.innerHTML =
        '<div class="yenb-card__media">' +
          (p.img ? '<img src="' + p.img + '" alt="" loading="lazy">' : '') +
          '<span class="yenb-card__pick" data-pick></span>' +
        '</div>' +
        '<h3 class="yenb-card__name">' + p.title + '</h3>' + note +
        '<p class="yenb-card__price"><b data-bundle-price></b><s>' + money(p.price) + '</s></p>' +
        '<div class="yenb-card__foot"></div>';
      el._data = p;
      // Tap anywhere on the card to add. The stepper handles its own clicks.
      el.addEventListener('click', function (ev) {
        if (ev.target.closest && ev.target.closest('.yenb-step')) return;
        add(p);
      });
      elCards.appendChild(el);
    });

    function countOf(hash) {
      var n = 0;
      for (var i = 0; i < picks.length; i++) if (picks[i].hash === hash) n++;
      return n;
    }
    function flash() {
      sticky.classList.remove('is-flash');
      void sticky.offsetWidth;            // restart the animation
      sticky.classList.add('is-flash');
    }
    function add(p) {
      if (isFull()) { flash(); return; }
      picks.push(p);
      render();
      flash();
      // First time the bundle completes on a phone, show them the total and
      // the button. Once only, so swapping items never keeps popping it up.
      if (isComplete() && !autoOpened && isMobile()) {
        autoOpened = true;
        setPanelOpen(true);
      }
    }
    function removeOne(hash) {
      for (var i = picks.length - 1; i >= 0; i--) {
        if (picks[i].hash === hash) { picks.splice(i, 1); break; }
      }
      render();
    }

    /* Quick start: the Best Sellers collection order, filtered to what the
       picker actually offers, topped up in picker order if that runs short. */
    function quickStart() {
      var byHandle = {};
      products.forEach(function (p) { byHandle[p.handle] = p; });
      var list = [];
      best.forEach(function (h) { if (byHandle[h] && list.indexOf(byHandle[h]) < 0) list.push(byHandle[h]); });
      products.forEach(function (p) { if (list.indexOf(p) < 0) list.push(p); });
      picks = list.slice(0, tier.min);
      render();
      flash();
      if (isMobile()) { autoOpened = true; setPanelOpen(true); }
    }
    elQuick.textContent = cfg.quickText;
    elQuick.addEventListener('click', quickStart);

    function renderSlots() {
      var n = picks.length;
      // Open tier: always one spare slot once the minimum is met, so it reads
      // as "keep going". Fixed tier: exactly the pack size.
      var slots = isOpen() ? Math.max(tier.min, n + 1) : tier.count;
      elSlots.style.gridTemplateColumns = 'repeat(' + Math.min(slots, 5) + ',minmax(0,1fr))';
      elSlots.innerHTML = '';
      for (var i = 0; i < slots; i++) {
        var wrap = document.createElement('div');
        wrap.className = 'yenb-slotwrap';
        var slot = document.createElement('div');
        slot.className = 'yenb-slot';
        var cap = document.createElement('span');
        cap.className = 'yenb-cap';
        if (picks[i]) {
          slot.classList.add('is-filled');
          if (picks[i].img) {
            var im = document.createElement('img');
            im.src = picks[i].img; im.alt = picks[i].title; im.loading = 'lazy';
            slot.appendChild(im);
          }
          var x = document.createElement('button');
          x.type = 'button'; x.className = 'yenb-slot__x';
          x.setAttribute('aria-label', 'Remove ' + picks[i].title);
          x.textContent = '×';
          (function (idx) { x.addEventListener('click', function () { picks.splice(idx, 1); render(); }); })(i);
          slot.appendChild(x);
          cap.textContent = picks[i].title;
        } else {
          // numbered, and never an empty div: the theme hides those
          var ghost = document.createElement('span');
          ghost.className = 'yenb-ghost';
          if (isOpen() && i >= tier.min) { ghost.textContent = '+'; slot.classList.add('is-more'); }
          else ghost.textContent = String(i + 1);
          slot.appendChild(ghost);
        }
        wrap.appendChild(slot); wrap.appendChild(cap);
        elSlots.appendChild(wrap);
      }
    }

    function renderCards() {
      var full = isFull();
      Array.prototype.forEach.call(elCards.children, function (el) {
        var p = el._data;
        var n = countOf(p.hash);
        el.classList.toggle('is-picked', n > 0);
        el.classList.toggle('is-full', full && n === 0);
        var badge = el.querySelector('[data-pick]');
        if (badge) badge.textContent = n > 1 ? String(n) : '✓';
        el.querySelector('[data-bundle-price]').textContent = money(roundItem(p.price, tier.discount));
        var foot = el.querySelector('.yenb-card__foot');
        foot.innerHTML = '';
        if (n === 0) {
          var b = document.createElement('button');
          b.type = 'button'; b.className = 'yenb-add'; b.textContent = full ? 'BUNDLE FULL' : 'ADD';
          b.disabled = full;
          foot.appendChild(b);
        } else {
          var box = document.createElement('div');
          box.className = 'yenb-step';
          var minus = document.createElement('button');
          minus.type = 'button'; minus.textContent = '−';
          minus.setAttribute('aria-label', 'Remove one ' + p.title);
          minus.addEventListener('click', function () { removeOne(p.hash); });
          var num = document.createElement('span'); num.textContent = String(n);
          var plus = document.createElement('button');
          plus.type = 'button'; plus.textContent = '+';
          plus.setAttribute('aria-label', 'Add one ' + p.title);
          plus.disabled = full;
          plus.addEventListener('click', function () { add(p); });
          box.appendChild(minus); box.appendChild(num); box.appendChild(plus);
          foot.appendChild(box);
        }
      });
    }

    /* Totals follow the exact cart lines: each line priced at its own pack's
       discount, each item rounded on its own. */
    function totals() {
      var sub = 0, total = 0, n = picks.length;
      var lines = n >= tier.min ? composeLines(tier, picks) : null;
      picks.forEach(function (p) { sub += p.price; });
      if (lines) {
        lines.forEach(function (l) {
          l.items.forEach(function (p) { total += roundItem(p.price, l.pack.discount); });
        });
      } else {
        picks.forEach(function (p) { total += roundItem(p.price, tier.discount); });
      }
      return { sub: sub, total: total, save: sub - total, lines: lines };
    }

    function renderTotals() {
      var n = picks.length, need = needed();
      var complete = isComplete();
      var t = totals();
      var idx = tiers.indexOf(tier);
      var next = idx >= 0 && idx < tiers.length - 1 ? tiers[idx + 1] : null;

      root.querySelector('[data-empty]').hidden = n > 0;
      root.querySelector('[data-totals]').hidden = n === 0;
      root.querySelector('[data-perks]').hidden = n === 0;
      root.querySelector('[data-sub]').textContent = money(t.sub);
      root.querySelector('[data-save-label]').textContent =
        'Bundle saving (' + (t.sub > 0 ? Math.round(t.save / t.sub * 100) : tier.discount) + '% off)';
      root.querySelector('[data-save]').textContent = '−' + money(t.save);
      root.querySelector('[data-total]').textContent = money(t.total);

      // Both perks are judged on what the customer actually pays, i.e. the
      // discounted total, which is what the cart will see.
      var giftOn = t.total >= cfg.giftCents, shipOn = t.total >= cfg.shipCents;
      root.querySelector('[data-gift]').classList.toggle('is-on', giftOn);
      root.querySelector('[data-ship]').classList.toggle('is-on', shipOn);
      root.querySelector('[data-gift-togo]').textContent = giftOn ? '' : money(cfg.giftCents - t.total) + ' to go';
      root.querySelector('[data-ship-togo]').textContent = shipOn ? '' : money(cfg.shipCents - t.total) + ' to go';

      elCount.textContent = isOpen() && n >= tier.min ? n + ' items' : n + '/' + tier.min;
      var bar = root.querySelector('[data-bar]');
      if (bar) bar.style.width = Math.round(Math.min(n / tier.min, 1) * 100) + '%';

      // Upgrade nudge: only once the bundle is complete and a bigger tier exists.
      if (complete && next) {
        var extra = next.min - picks.length;
        root.querySelector('[data-nudge-text]').textContent =
          'Add ' + extra + ' more and save ' + next.discount + '% on everything';
        nudgeBtn.textContent = 'Make it ' + next.label.replace(' items', '');
        nudgeBtn.onclick = function () { setTier(next); };
        nudge.hidden = false;
      } else {
        nudge.hidden = true;
      }

      elCta.disabled = !complete;
      elCta.textContent = complete
        ? 'ADD BUNDLE TO CART  ·  ' + money(t.total)
        : 'PICK ' + need + ' MORE';

      /* sticky bar (phones) */
      root.querySelector('[data-sticky-title]').textContent =
        n === 0 ? 'Build your ' + tier.label.replace(' items', '') + ' pack'
        : isOpen() && n >= tier.min ? n + ' items picked'
        : n + ' of ' + tier.min + ' picked';
      root.querySelector('[data-sticky-sub]').textContent =
        n === 0 ? tierSaveLabel(tier) + ' on ' + (isOpen() ? 'any ' + tier.min + ' or more' : 'any ' + tier.min + ' items')
        : complete ? money(t.total) + ' · you save ' + money(t.save)
        : money(t.total) + ' · pick ' + need + ' more';
      stickyCta.textContent = complete ? 'Add to cart' : 'View bundle';
      stickyCta.classList.toggle('is-view', !complete);
      elView.textContent = 'View bundle  ·  ' + (isOpen() && n >= tier.min ? n + ' items' : n + '/' + tier.min);
    }

    function render() { renderTiers(); renderSlots(); renderCards(); renderTotals(); }

    /* ---------- add to cart ---------- */
    var adding = false;
    function includesText(items) {
      // A visible line item property (no leading underscore) so the cart drawer
      // and checkout list what is inside the bundle. "2 x Name" for duplicates.
      var counts = {}, order = [];
      items.forEach(function (p) {
        if (counts[p.hash]) { counts[p.hash]++; } else { counts[p.hash] = 1; order.push(p); }
      });
      return order.map(function (p) {
        return (counts[p.hash] > 1 ? counts[p.hash] + ' x ' : '') + p.title;
      }).join(', ');
    }
    function cartItems() {
      var lines = composeLines(tier, picks);
      if (!lines) return null;
      return lines.map(function (l) {
        return {
          id: Number(l.pack.variantId),
          quantity: 1,
          properties: {
            _bundle_selection: l.items.map(function (p) { return p.hash; }).join(' <> '),
            Includes: includesText(l.items)
          }
        };
      });
    }
    function addToCart() {
      if (adding || !isComplete()) return;
      var items = cartItems();
      if (!items) return;
      adding = true;
      var label = elCta.textContent, sLabel = stickyCta.textContent;
      elCta.disabled = true; stickyCta.disabled = true;
      elCta.textContent = 'ADDING...'; stickyCta.textContent = 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function () {
          adding = false;
          picks = [];
          render();
          stickyCta.disabled = false;
          if (isMobile()) setPanelOpen(false);
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
          window.location.href = '/cart';
        })
        .catch(function () {
          adding = false;
          elCta.disabled = false; stickyCta.disabled = false;
          elCta.textContent = label; stickyCta.textContent = sLabel;
          window.location.href = '/cart';
        });
    }
    elCta.addEventListener('click', addToCart);
    stickyCta.addEventListener('click', function () {
      if (isComplete()) addToCart(); else setPanelOpen(true);
    });

    /* ---------- phone drawer ---------- */
    function setPanelOpen(open) {
      panel.dataset.open = open ? 'true' : 'false';
      // Lets the stylesheet hide the Aura Agent bubble while the drawer covers it.
      document.body.classList.toggle('yenb-panel-open', !!open);
    }
    root.querySelector('[data-sticky-open]').addEventListener('click', function () { setPanelOpen(true); });
    elView.addEventListener('click', function () {
      if (isMobile()) { setPanelOpen(true); return; }
      panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      panel.classList.remove('is-pulse');
      void panel.offsetWidth;
      panel.classList.add('is-pulse');
    });
    root.querySelector('[data-close]').addEventListener('click', function () { setPanelOpen(false); });
    root.querySelector('[data-backdrop]').addEventListener('click', function () { setPanelOpen(false); });
    setPanelOpen(!isMobile());
    var onMq = function () { setPanelOpen(!isMobile()); };

    // Desktop: keep the sticky panel clear of the theme's fixed header.
    var stickMax = 0;
    function stickTop() {
      var h = document.querySelector('.shopify-section-header-sticky, .shopify-section-header, .section-header');
      var px = h ? h.getBoundingClientRect().height : 0;
      if (px > stickMax) stickMax = px;
      root.style.setProperty('--yenb-stick', Math.round(stickMax + 16) + 'px');
    }
    stickTop();
    window.addEventListener('resize', stickTop);
    window.setTimeout(stickTop, 1500);   // header settles after fonts/announcement bar load
    if (MQ.addEventListener) MQ.addEventListener('change', onMq); else if (MQ.addListener) MQ.addListener(onMq);

    // The shared PDP sections ship a CTA whose inline onclick scrolls to
    // '.pdp-gift-banner', which only exists on the soap bar template. Here it
    // found nothing and fell through to document.body, i.e. it jumped to the
    // top. Point them at the builder instead.
    Array.prototype.forEach.call(document.querySelectorAll('.pdp-cta-btn'), function (b) {
      b.removeAttribute('onclick');
      b.addEventListener('click', function (ev) {
        ev.preventDefault();
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    render();
    document.body.classList.add('yenb-ready');   // now it is safe to hide theirs
    return true;
  }

  /* ---------- wait for the app, then take over ---------- */
  var waited = 0;
  (function tick() {
    if (dataReady() && build()) return;
    waited += POLL_MS;
    // If the app never renders we simply do nothing and its own builder,
    // which is only moved off-screen, is still what the customer would get.
    if (waited < TIMEOUT_MS) window.setTimeout(tick, POLL_MS);
  })();
})();
